import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../config/apiConfig';
import { downloadSiteContentModule } from '../config/siteContentExport';
import BlogAdmin from './BlogAdmin';
import UsersAdmin from './UsersAdmin';
import { bundledSiteContent, normalizeSiteContent, resolveSiteImage } from '../context/SiteContentContext';

const sectionNames = {
  hero: 'Portada', lead: 'Introducción', about: 'Sobre mí', method: 'Método',
  services: 'Encabezado de servicios', callToAction: 'Llamado a la acción',
  recommendations: 'Recursos', testimonials: 'Testimonios', contact: 'Contacto'
};
const fieldNames = {
  eyebrow: 'Etiqueta', title: 'Título', description: 'Descripción', primaryButton: 'Botón principal',
  secondaryButton: 'Botón secundario', button: 'Texto del botón', paragraphs: 'Párrafos'
};
const imageNames = { logo: 'Logo', hero: 'Portada', about: 'Sobre mí', method: 'Llamado a la acción', services: 'Servicios', contact: 'Contacto' };

function cloneDefaultContent() {
  return JSON.parse(JSON.stringify(bundledSiteContent));
}

function SiteAdmin() {
  const [token, setToken] = useState(() => window.localStorage.getItem('babyplanner_admin_token') || '');
  const [tokenInput, setTokenInput] = useState(token);
  const [content, setContent] = useState(cloneDefaultContent);
  const [activeTab, setActiveTab] = useState('sections');
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(Boolean(token));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState('');
  const [exporting, setExporting] = useState(false);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    fetch(`${API_BASE_URL}/api/admin/site-content`, { headers: authHeaders })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'No se pudo cargar el contenido.');
        setContent(normalizeSiteContent(data.content));
        setStatus({ type: '', message: '' });
      })
      .catch((error) => setStatus({
        type: 'warning',
        message: error instanceof TypeError
          ? 'El backend no está disponible. Estás editando el snapshot incluido en el sitio; puedes exportarlo para GitHub Pages.'
          : error.message
      }))
      .finally(() => setLoading(false));
  }, [token, authHeaders]);

  const updateSection = (sectionKey, field, value) => {
    setContent((current) => ({
      ...current,
      sections: { ...current.sections, [sectionKey]: { ...current.sections[sectionKey], [field]: value } }
    }));
  };

  const updateListItem = (list, index, field, value) => {
    setContent((current) => ({
      ...current,
      [list]: current[list].map((item, itemIndex) => itemIndex === index ? { ...item, [field]: value } : item)
    }));
  };

  const removeListItem = (list, index) => {
    setContent((current) => ({ ...current, [list]: current[list].filter((_, itemIndex) => itemIndex !== index) }));
  };

  const addService = () => setContent((current) => ({
    ...current,
    services: [...current.services, { title: 'Nuevo servicio', subtitle: 'Subtítulo', description: '', items: [] }]
  }));

  const saveContent = async () => {
    setSaving(true);
    setStatus({ type: '', message: '' });
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/site-content`, {
        method: 'PUT', headers: { ...authHeaders, 'Content-Type': 'application/json' }, body: JSON.stringify({ content })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'No se pudieron guardar los cambios.');
      setContent(normalizeSiteContent(data.content));
      setStatus({ type: 'success', message: 'Cambios publicados correctamente.' });
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = (slot, file) => {
    if (!file) return;
    setUploading(slot);
    setStatus({ type: '', message: '' });
    const reader = new FileReader();
    reader.onload = async () => {
      const localDataUrl = reader.result;
      setContent((current) => ({
        ...current,
        images: { ...current.images, [slot]: { ...current.images[slot], src: localDataUrl } }
      }));
      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/site-content/images`, {
          method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify({ slot, fileName: file.name, dataUrl: localDataUrl })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'No se pudo subir la imagen.');
        setContent((current) => ({
          ...current,
          images: { ...current.images, [slot]: { ...current.images[slot], src: data.url } }
        }));
        setStatus({ type: 'success', message: 'Imagen cargada. Guarda los cambios para publicarla.' });
      } catch (error) {
        setStatus({
          type: 'warning',
          message: 'El backend no recibió la foto, pero quedó incorporada localmente. Puedes exportar el módulo para GitHub Pages.'
        });
      } finally {
        setUploading('');
      }
    };
    reader.onerror = () => {
      setUploading('');
      setStatus({ type: 'error', message: 'No se pudo leer la imagen.' });
    };
    reader.readAsDataURL(file);
  };

  const exportModule = async () => {
    setExporting(true);
    setStatus({ type: '', message: '' });
    try {
      await downloadSiteContentModule(content);
      setStatus({
        type: 'success',
        message: 'Módulo descargado. Reemplaza src/config/siteContent.snapshot.js con ese archivo y vuelve a desplegar GitHub Pages.'
      });
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setExporting(false);
    }
  };

  const handleLogin = (event) => {
    event.preventDefault();
    const nextToken = tokenInput.trim();
    window.localStorage.setItem('babyplanner_admin_token', nextToken);
    setToken(nextToken);
  };

  const logout = () => {
    window.localStorage.removeItem('babyplanner_admin_token');
    setToken(''); setTokenInput(''); setStatus({ type: '', message: '' });
  };

  if (!token) {
    return <main className="page-shell admin-page"><section className="admin-login-card">
      <p className="eyebrow">Site admin</p><h2>Administrar el sitio</h2>
      <p className="section-lede">Usa el mismo token del panel de mensajería.</p>
      <form className="form-grid" onSubmit={handleLogin}><div className="form-field">
        <label htmlFor="site-admin-token">Token</label><input id="site-admin-token" type="password" value={tokenInput} onChange={(event) => setTokenInput(event.target.value)} required />
      </div><button className="button button-primary" type="submit">Entrar</button></form>
    </section></main>;
  }

  return <main className="page-shell admin-page"><section className="admin-shell site-admin-shell">
    <header className="admin-topbar"><div><p className="eyebrow">Site admin</p><h2>Contenido del sitio</h2></div>
      <div className="site-admin-top-actions"><button className="button button-secondary" type="button" onClick={exportModule} disabled={exporting}>{exporting ? 'Exportando…' : 'Exportar módulo'}</button><a className="button button-secondary" href="/#/chat-admin">Mensajería</a><a className="button button-secondary" href="/" target="_blank" rel="noreferrer">Ver sitio</a><button className="button button-secondary" type="button" onClick={logout}>Salir</button></div>
    </header>
    <nav className="admin-tabs" aria-label="Contenido editable">
      {[['sections', 'Secciones'], ['blog', 'Blog de suscriptores'], ['users', 'Usuarios'], ['services', 'Servicios'], ['images', 'Fotos'], ['extras', 'Otros contenidos']].map(([key, label]) =>
        <button type="button" key={key} className={activeTab === key ? 'is-active' : ''} onClick={() => setActiveTab(key)}>{label}</button>)}
    </nav>
    {status.message && <p className={`site-admin-status ${status.type}`} role="status">{status.message}</p>}
    {loading ? <p className="admin-empty site-admin-loading">Cargando contenido…</p> : <div className="site-admin-content">
      {activeTab === 'blog' && <BlogAdmin token={token} />}
      {activeTab === 'users' && <UsersAdmin token={token} />}
      {activeTab === 'sections' && <div className="site-editor-grid">{Object.entries(content.sections).map(([sectionKey, fields]) =>
        <fieldset className="site-editor-card" key={sectionKey}><legend>{sectionNames[sectionKey] || sectionKey}</legend>
          {Object.entries(fields).map(([field, value]) => <label key={field}>{fieldNames[field] || field}
            {Array.isArray(value) ? <textarea rows="5" value={value.join('\n\n')} onChange={(event) => updateSection(sectionKey, field, event.target.value.split(/\n\s*\n/).filter(Boolean))} />
              : (String(value).length > 80 || field === 'description' || field === 'title') ? <textarea rows="3" value={value} onChange={(event) => updateSection(sectionKey, field, event.target.value)} />
              : <input value={value} onChange={(event) => updateSection(sectionKey, field, event.target.value)} />}
          </label>)}</fieldset>)}</div>}

      {activeTab === 'services' && <div><div className="site-editor-heading"><div><h3>Servicios</h3><p>Edita sus detalles, reordena los puntos o agrega nuevos servicios.</p></div><button type="button" className="button button-secondary" onClick={addService}>Agregar servicio</button></div>
        <div className="site-editor-grid">{content.services.map((service, index) => <fieldset className="site-editor-card" key={index}><legend>Servicio {index + 1}</legend>
          <label>Título<input value={service.title} onChange={(event) => updateListItem('services', index, 'title', event.target.value)} /></label>
          <label>Subtítulo<input value={service.subtitle} onChange={(event) => updateListItem('services', index, 'subtitle', event.target.value)} /></label>
          <label>Descripción<textarea rows="4" value={service.description} onChange={(event) => updateListItem('services', index, 'description', event.target.value)} /></label>
          <label>Características (una por línea)<textarea rows="5" value={service.items.join('\n')} onChange={(event) => updateListItem('services', index, 'items', event.target.value.split('\n').filter(Boolean))} /></label>
          <button type="button" className="site-remove-button" onClick={() => removeListItem('services', index)}>Eliminar servicio</button>
        </fieldset>)}</div></div>}

      {activeTab === 'images' && <div className="site-image-grid">{Object.entries(content.images).map(([slot, imageData]) => <article className="site-image-card" key={slot}>
        <img src={resolveSiteImage(imageData.src)} alt={imageData.alt || ''} /><div><h3>{imageNames[slot] || slot}</h3>
          <label>Texto alternativo<input value={imageData.alt} onChange={(event) => setContent((current) => ({ ...current, images: { ...current.images, [slot]: { ...current.images[slot], alt: event.target.value } } }))} /></label>
          <label className="site-file-button">{uploading === slot ? 'Subiendo…' : 'Elegir nueva foto'}<input type="file" accept="image/jpeg,image/png,image/webp,image/gif" disabled={Boolean(uploading)} onChange={(event) => uploadImage(slot, event.target.files[0])} /></label>
        </div></article>)}</div>}

      {activeTab === 'extras' && <div className="site-editor-grid">
        {content.methodSteps.map((step, index) => <fieldset className="site-editor-card" key={`method-${index}`}><legend>Método {index + 1}</legend><label>Título<input value={step.title} onChange={(event) => updateListItem('methodSteps', index, 'title', event.target.value)} /></label><label>Detalle<textarea value={step.copy} onChange={(event) => updateListItem('methodSteps', index, 'copy', event.target.value)} /></label></fieldset>)}
        {content.recommendations.map((item, index) => <fieldset className="site-editor-card" key={`recommendation-${index}`}><legend>Recurso {index + 1}</legend><label>Título<input value={item.title} onChange={(event) => updateListItem('recommendations', index, 'title', event.target.value)} /></label><label>Detalle<textarea value={item.copy} onChange={(event) => updateListItem('recommendations', index, 'copy', event.target.value)} /></label><label>Elementos (uno por línea)<textarea value={item.items.join('\n')} onChange={(event) => updateListItem('recommendations', index, 'items', event.target.value.split('\n').filter(Boolean))} /></label></fieldset>)}
        {content.testimonials.map((item, index) => <fieldset className="site-editor-card" key={`testimonial-${index}`}><legend>Testimonio {index + 1}</legend><label>Nombre<input value={item.name} onChange={(event) => updateListItem('testimonials', index, 'name', event.target.value)} /></label><label>Testimonio<textarea value={item.quote} onChange={(event) => updateListItem('testimonials', index, 'quote', event.target.value)} /></label></fieldset>)}
      </div>}
    </div>}
    <footer className="site-admin-savebar"><span>Backend: guarda al instante. GitHub Pages: exporta el snapshot y vuelve a desplegar.</span><button className="button button-secondary" type="button" onClick={exportModule} disabled={exporting}>{exporting ? 'Exportando…' : 'Exportar para GitHub Pages'}</button><button className="button button-primary" type="button" onClick={saveContent} disabled={saving || loading}>{saving ? 'Guardando…' : 'Guardar en backend'}</button></footer>
  </section></main>;
}

export default SiteAdmin;
